import axios from "axios";
import { getMessageContentFromResponse } from "./Chatapi";
import { VISION_EXTRACTION_BASE } from "./prompts/LearningSupportsVisionPrompt";
import { VISION_EXTRACTION_TAIL } from "./prompts/LearningSupportsPrompts";
import { MEASURABLE_GOALS_SYSTEM } from "./prompts/Prompts_MEASURABLE_GOALS_SYSTEM";
import { SUPPORTS_SERVICES_SYSTEM } from "./prompts/Prompts_SUPPORTS_SERVICES_SYSTEM";
import { CORE_COMPETENCY_GOALS_SYSTEM } from "./prompts/Prompts_CORE_COMPETENCY_GOALS_SYSTEM";
import { getSystemPrompt } from "./prompts/curricularCompetencyGoals/systemPrompt";
import { getUserPrompt } from "./prompts/curricularCompetencyGoals/userPrompt";
import { STRENGTHS_NEEDS_DESIGNATION_ALIGNED_SYSTEM } from "./prompts/Prompts_STRENGTHS_NEEDS_DESIGNATION_ALIGNED";
import { compressPdfFiles, getPdfQualitySettings, getWordQualitySettings } from "./pdfCompression";
import { getCurricularGoalsBankFromXlsx } from "./learningSupportData/curricularGoalsFromXlsx";
import { buildClientSecurityHeaders } from "./clientRequestSecurity";

const API_URL_BASE = process.env.REACT_APP_BASE_URL_BASE;
// OpenAI replacement for retired gpt-4.1-nano-2025-04-14 (shutdown 2026-10-23).
const GENERATE_WITH_AI_MODEL = "gpt-5.6-luna";

function ensureLearningSupportsApiConfigured() {
  if (!API_URL_BASE) {
    throw new Error("AI service is not configured. Please contact your administrator.");
  }
}

/** Request config with Bearer + client API key/hash (added before send). */
async function getLearningSupportsRequestConfig(extra = {}) {
  const securityHeaders = await buildClientSecurityHeaders();
  const headers = {
    "Content-Type": "application/json",
    ...securityHeaders,
    ...(extra.headers || {}),
  };
  const token = localStorage.getItem("access_token");
  if (token && token !== "null" && token !== "undefined") {
    headers.Authorization = `Bearer ${token}`;
  }
  return {
    timeout: 60000,
    ...extra,
    headers,
  };
}

function extractApiErrorText(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || "";
  if (typeof data === "string") return data;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.error?.message === "string") return data.error.message;
  try {
    return JSON.stringify(data);
  } catch {
    return "";
  }
}

function isProviderErrorText(text) {
  return /openai|azure|deprecated|deprecat|invalid_request_error|invalid_api_key|invalid_organization|model [`'"]?gpt-|gpt-\d/i.test(
    text || ""
  );
}

/**
 * Teacher-facing message only. Provider names, model ids, and raw API JSON stay in the console.
 */
export function formatLearningSupportsAiError(error, fallback) {
  const status = error?.response?.status;
  const apiText = extractApiErrorText(error);
  const ownMessage = error?.message || "";
  console.error("Learning Supports AI error:", status || "", apiText || ownMessage);

  if (status === 429) {
    return "Too many requests right now. Please wait a moment and try again.";
  }
  if (error?.code === "ECONNABORTED" || /timeout/i.test(ownMessage)) {
    return "This is taking longer than expected. Please try again.";
  }
  if ((status === 401 || status === 403) && !isProviderErrorText(apiText)) {
    return "Your session may have expired. Please sign in again, then try again.";
  }
  if (!error?.response && ownMessage && !isProviderErrorText(ownMessage)) {
    return ownMessage;
  }
  return fallback || "We couldn't generate this right now. Please try again in a moment.";
}

/**
 * Build strengths/needs table text for prompts. Only includes domains that have
 * at least strengths OR needs text (excludes empty/partial rows like Behaviour with no text).
 */
function buildStrengthsNeedsTableText(strengthsNeedsTable) {
  const snTable = strengthsNeedsTable["strengths_needs_by_domain"] || strengthsNeedsTable["ispp_domain_summary"] || {};
  return Object.entries(snTable)
    .map(([domain, row]) => {
      const s = (row?.strength || "").trim();
      const n = (row?.need || "").trim();
      const p = (row?.priority || "").trim();
      if (!s && !n) return null;
      return `${domain}: Strengths: ${s || "—"}; Needs: ${n || "—"}; ${p ? `Priority: ${p}` : ""}`;
    })
    .filter(Boolean)
    .join("\n");
}

/**
 * Build full student data string from all answers (typed + scanned). Includes every question that has a value.
 * @param {{ [question: string]: any }} answers - answers keyed by question text
 * @param {Array<{ question: string, key: string, type?: string }>} questionsList
 * @param {object} strengthsNeedsTable - optional table data
 * @param {string} [excludeKey] - question key to omit (e.g. the field we are generating)
 */
function buildAllStudentDataForPrompt(answers, questionsList, strengthsNeedsTable = {}, excludeKey) {
  const lines = [];
  for (const q of questionsList || []) {
    if (excludeKey && (q.key === excludeKey || q.question === excludeKey)) continue;
    const raw = answers[q.question];
    if (raw === undefined || raw === null) continue;
    const val = typeof raw === "object" && !Array.isArray(raw)
      ? JSON.stringify(raw)
      : Array.isArray(raw)
        ? raw.join(", ")
        : String(raw).trim();
    if (val === "") continue;
    lines.push(`${q.question} (${q.key}): ${val}`);
  }
  const tableText = buildStrengthsNeedsTableText(strengthsNeedsTable);
  if (tableText) lines.push("\nStrengths/needs by domain table:\n" + tableText);
  return lines.length ? lines.join("\n") : "";
}

// Detect if file is Word (.doc or .docx)
const isWordDocument = (file) => {
  const name = (file.name || "").toLowerCase();
  const type = file.type || "";
  return (
    name.endsWith(".docx") ||
    name.endsWith(".doc") ||
    type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    type === "application/msword"
  );
};

// Parse ChatGPT response and return { extracted_answers }
function parseExtractedAnswersResponse(response) {
  const content = getMessageContentFromResponse(response);
  if (!content) throw new Error("No response from ChatGPT API.");
  const trimmed = content.trim();
  // Model returned plain text (e.g. "I'm unable to...") instead of JSON
  if (
    trimmed.startsWith("I'm ") ||
    trimmed.startsWith("I am ") ||
    trimmed.startsWith("I cannot") ||
    trimmed.startsWith("I can't") ||
    trimmed.startsWith("Sorry") ||
    trimmed.startsWith("Error") ||
    trimmed.startsWith("Unable")
  ) {
    throw new Error(
      "The document could not be read as a questionnaire. The assistant did not return valid data. Please ensure the file is a clear IEP or survey form (PDF/Word) and try again."
    );
  }
  let jsonString = trimmed;
  if (jsonString.startsWith("```")) {
    jsonString = jsonString.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
  }
  const jsonMatch = jsonString.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error(
      "No questionnaire data was extracted from the document. The response was not valid JSON. Please try a different file or ensure the document contains clear form/survey content."
    );
  }
  let extractedAnswers;
  try {
    extractedAnswers = JSON.parse(jsonMatch[0]);
  } catch (e) {
    throw new Error(
      "The extracted data could not be parsed. Please try again or use a clearer document."
    );
  }
  return { extracted_answers: extractedAnswers };
}

// Single file: PDF or Word → images → single prompt → ChatGPT Vision (one API call)
export const processLearningSupportsPDF = async (file, questionsList) => {
  return processLearningSupportsDocuments([file], questionsList);
};

// Multiple files (PDF + DOC ek sath): sab files → images → ek hi API call → ChatGPT Vision
// Optional signal: pass AbortSignal to cancel the request (e.g. when user uploads more documents)

export const processLearningSupportsDocuments = async (files, questionsList, signal) => {
  try {
    const questionsStructure = questionsList.map(q => ({
      key: q.key,
      question: q.question,
      type: q.type,
      options: q.options?.map(opt => opt.label) || []
    }));

    // Compress PDFs before processing (can reduce size and speed up read/upload)
    const filesToProcess = await compressPdfFiles(files);
    // const filesToProcess = files

    const allImageUrls = [];
    for (const file of filesToProcess) {
      const imageUrls = await fileToImages(file);
      if (imageUrls.length) allImageUrls.push(...imageUrls);
    }
    if (!allImageUrls.length) throw new Error("No content could be converted to images from the document(s).");
    return await extractAnswersFromImages(allImageUrls, questionsStructure, signal);
  } catch (error) {
    console.error("Error processing Learning Supports document(s):", error);
    throw error;
  }
};

// Detect if file is an image (.jpg, .jpeg, .png)
const isImageFile = (file) => {
  const name = (file.name || "").toLowerCase();
  const type = file.type || "";
  return (
    type.startsWith("image/") ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".png")
  );
};

async function imageFileToDataUrl(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Could not read image file "${file.name}".`));
    reader.readAsDataURL(file);
  });
  return dataUrl ? [dataUrl] : [];
}

// PDF or Word → array of base64 image data URLs (single path for both)
async function fileToImages(file) {
  if (isWordDocument(file)) return await renderWordToImages(file);
  if (isImageFile(file)) return await imageFileToDataUrl(file);
  return await renderPDFPagesToImages(file);
}

// Word (.doc/.docx) → HTML via mammoth → capture as image(s) via html2canvas.
// Uses quality check (file size): small docs = higher quality, large = reduced image for upload.
async function renderWordToImages(file) {
  const loadMammoth = () => {
    if (typeof window !== "undefined" && window.mammoth) return Promise.resolve(window.mammoth);
    return new Promise((res, rej) => {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js";
      script.onload = () => res(window.mammoth);
      script.onerror = () => rej(new Error("Failed to load mammoth.js."));
      document.head.appendChild(script);
    });
  };
  const loadHtml2Canvas = () => {
    if (typeof window !== "undefined" && window.html2canvas) return Promise.resolve(window.html2canvas);
    return new Promise((res, rej) => {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
      script.onload = () => res(window.html2canvas);
      script.onerror = () => rej(new Error("Failed to load html2canvas."));
      document.head.appendChild(script);
    });
  };

  const [mammoth, html2canvas] = await Promise.all([loadMammoth(), loadHtml2Canvas()]);
  const arrayBuffer = await file.arrayBuffer();
  const { value: html } = await mammoth.convertToHtml({ arrayBuffer });
  if (!html || !html.trim()) throw new Error("No content could be extracted from the Word document.");

  const { scale, jpegQuality } = getWordQualitySettings(file.size);

  const wrap = document.createElement("div");
  wrap.style.cssText = "position:absolute;left:-9999px;top:0;width:800px;background:#fff;padding:40px;box-sizing:border-box;font-family:Arial,sans-serif;font-size:14px;line-height:1.5;color:#000;";
  wrap.innerHTML = html;
  document.body.appendChild(wrap);

  try {
    const canvas = await html2canvas(wrap, { scale, useCORS: true, logging: false });
    const dataUrl = canvas.toDataURL("image/jpeg", jpegQuality);
    return dataUrl ? [dataUrl] : [];
  } finally {
    document.body.removeChild(wrap);
  }
}

// Render PDF pages to base64 images using PDF.js (for Vision API).
// Checks quality first (file size + page count): small PDFs keep higher quality, large ones use reduced size.
async function renderPDFPagesToImages(file) {
  const loadPdfJs = () => {
    if (typeof window !== "undefined" && window.pdfjsLib) {
      return Promise.resolve(window.pdfjsLib);
    }
    return new Promise((res, rej) => {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        res(window.pdfjsLib);
      };
      script.onerror = () => rej(new Error("Failed to load PDF.js."));
      document.head.appendChild(script);
    });
  };

  const pdfjsLib = await loadPdfJs();
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  const { scale, jpegQuality } = getPdfQualitySettings(file.size, pdf.numPages);
  const imageUrls = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
    const dataUrl = canvas.toDataURL("image/jpeg", jpegQuality);
    imageUrls.push(dataUrl);
  }

  return imageUrls;
}

// Build vision message content: text prompt + all page images
function buildVisionContent(questionsStructure, imageUrls) {
  const textPrompt =
    VISION_EXTRACTION_BASE +
    JSON.stringify(questionsStructure, null, 2) +
    VISION_EXTRACTION_TAIL;
  
  console.log(textPrompt, "textPrompt 1000")
  const content = [
    { type: "input_text", text: textPrompt },
    ...imageUrls.map((url) => ({ type: "input_image", image_url: url })),
  ];
  return content;
}

// Images (from PDF or Word) → single prompt → ChatGPT Vision API
async function extractAnswersFromImages(imageUrls, questionsStructure, signal) {
  const config = { headers: { "Content-Type": "application/json" } };
  if (signal) config.signal = signal;
  const payload = {
    model: "gpt-4.1-mini",
    input: [
      {
        role: "user",
        content: buildVisionContent(questionsStructure, imageUrls),
      }
    ],
    max_output_tokens: 800,
    text: {
      format: {
        type: "json_object"
      }
    }
  };
  
  const response = await axios.post(
    `${API_URL_BASE}/api/chatgpt/azure/openAI/responses`,
    payload,
    await getLearningSupportsRequestConfig(config)
  );

  console.log(response, "response 1000")
  console.log(parseExtractedAnswersResponse(response), "parseExtractedAnswersResponse 1000")
  return parseExtractedAnswersResponse(response);
}

/**
 * Generate Q7: Strengths and needs across designation-aligned domains and BC core competencies.
 */
export const generateStrengthsNeedsDesignationAligned = async ({ answers = {}, strengthsNeedsTable = {}, questionsList = [] }) => {
  ensureLearningSupportsApiConfigured();
  const getAnswer = (keyOrQuestion) => {
    const q = questionsList.find((x) => x.key === keyOrQuestion || x.question === keyOrQuestion);
    if (!q) return "";
    return (answers[q.question] || "").trim();
  };

  const designation = getAnswer("designation_category_funding") || getAnswer("student_profile_primary_designation") || "";
  const studentName = getAnswer("student_profile_first_last_name") || "";
  const grade = getAnswer("student_profile_grade") || "";
  const learningPrefs = getAnswer("student_profile_learning_preferences") || "";
  const interests = getAnswer("student_profile_interests") || "";

  const tableText = buildStrengthsNeedsTableText(strengthsNeedsTable);

  const userPrompt = `Student: ${studentName || "Not specified"}. Grade: ${grade || "Not specified"}.
Designation/Category: ${designation || "Not specified"}

Learning preferences: ${learningPrefs || "Not provided"}
Interests: ${interests || "Not provided"}

Strengths/needs by domain table (if any):
${tableText || "Not provided"}

Generate strengths and needs across designation-aligned domains and BC core competencies as described.`;

  try {
    const response = await axios.post(
      `${API_URL_BASE}/api/chatgpt/openAI/completions`,
      {
        model: GENERATE_WITH_AI_MODEL,
        reasoning_effort: "low",
        messages: [
          { role: "system", content: STRENGTHS_NEEDS_DESIGNATION_ALIGNED_SYSTEM },
          { role: "user", content: userPrompt }
        ],
        max_completion_tokens: 1200,
      },
      await getLearningSupportsRequestConfig()
    );
    const content = getMessageContentFromResponse(response);
    return (content || "").trim();
  } catch (error) {
    console.error("Error generating strengths/needs designation aligned:", error);
    throw error;
  }
};

/**
 * Generate Core Competency goals (Communication, Thinking, Personal & Social).
 */
export const generateCoreCompetencyGoals = async ({ answers = {}, strengthsNeedsTable = {}, questionsList = [] }) => {
  ensureLearningSupportsApiConfigured();
  const allStudentData = buildAllStudentDataForPrompt(answers, questionsList, strengthsNeedsTable, "student_profile_core_competency_goals");

  const userPrompt = `All available student data (typed or scanned — use everything below to decide goals and "Why chosen"):

${allStudentData || "No data provided."}

---

Generate 2–4 Core Competency goals (Communication, Thinking, Personal & Social) as described. Use only the exact goal phrases from the system instructions; for each goal state the Core Competency name and a short "Why chosen:" citing the data above. If the data above is empty or insufficient, reply with "Not sufficient data."`;

console.log(userPrompt,"userPrompt ") 
try {
    const response = await axios.post(
      `${API_URL_BASE}/api/chatgpt/openAI/completions`,
      {
        model: GENERATE_WITH_AI_MODEL,
        reasoning_effort: "low",
        messages: [
          { role: "system", content: CORE_COMPETENCY_GOALS_SYSTEM },
          { role: "user", content: userPrompt }
        ],
        max_completion_tokens: 1000,
      },
      await getLearningSupportsRequestConfig()
    );
    const content = getMessageContentFromResponse(response);
    return (content || "").trim();
  } catch (error) {
    console.error("Error generating Core Competency goals:", error);
    throw error;
  }
};

/**
 * Generate Curricular Competency goals (subject-area competencies).
 * Goal bank is loaded from the XLSX file (ELA K-11, Numeracy K-9, and Replacement) and passed into the prompt so goals use exact wording from the data.
 */
export const generateCurricularCompetencyGoals = async ({ answers = {}, strengthsNeedsTable = {}, questionsList = [] }) => {
  ensureLearningSupportsApiConfigured();
  const allStudentData = buildAllStudentDataForPrompt(answers, questionsList, strengthsNeedsTable, "student_profile_curricular_competency_goals");

  const goalBankText = await getCurricularGoalsBankFromXlsx();
  // Console: Excel wala data yahan pass ho raha hai — system prompt mein jaata hai
  console.log("[Curricular Goals] Excel data (goalBankText) — length:", goalBankText?.length ?? 0);
   
  if (goalBankText) {
    console.log("[Curricular Goals] Excel data preview:", goalBankText);
  } else {
    console.warn("[Curricular Goals] Excel data empty — check public/learningSupportData/curricular_goals.xlsx");
  }
  const systemContent = getSystemPrompt(goalBankText);
  const userPrompt = getUserPrompt(allStudentData);

  try {
    const response = await axios.post(
      `${API_URL_BASE}/api/chatgpt/openAI/completions`,
      {
        model: GENERATE_WITH_AI_MODEL,
        reasoning_effort: "low",
        messages: [
          { role: "system", content: systemContent },
          { role: "user", content: userPrompt }
        ],
        max_completion_tokens: 1000,
      },
      await getLearningSupportsRequestConfig()
    );
    const content = getMessageContentFromResponse(response);
    return (content || "").trim();
  } catch (error) {
    console.error("Error generating Curricular Competency goals:", error);
    throw error;
  }
};

/**
 * Generate measurable, strength-based goals for Q9.
 * Pulls user-selected Core Competencies and Curricular Competencies (from Q5). Under each chosen
 * competency, generates 1–2 specific, measurable objectives with: methods of measurement,
 * current level of performance (baseline from all student input), and proficiency scale.
 */
export const generateMeasurableGoals = async ({ answers = {}, strengthsNeedsTable = {}, questionsList = [] }) => {
  ensureLearningSupportsApiConfigured();
  const getAnswer = (keyOrQuestion) => {
    const q = questionsList.find((x) => x.key === keyOrQuestion || x.question === keyOrQuestion);
    if (!q) return "";
    return (answers[q.question] || "").trim();
  };

  // Q5: User-selected Core and Curricular Competency goals (chosen competencies to draft goals under)
  const coreCompetencyGoals = getAnswer("student_profile_core_competency_goals") || "";
  const curricularCompetencyGoals = getAnswer("student_profile_curricular_competency_goals") || "";

  // Full student data for baseline (reports, ISPP, designation, strengths/needs, etc.)
  const allStudentData = buildAllStudentDataForPrompt(
    answers,
    questionsList,
    strengthsNeedsTable,
    "measurable_strength_based_goals"
  );

  const userPrompt = `User-selected Core Competency goals (from Q5 — use these as the Core Competencies to address):
${coreCompetencyGoals || "Not provided. Skip Core Competency goals or state that none were selected."}

User-selected Curricular Competency goals (from Q5 — use these as the Curricular Competencies to address):
${curricularCompetencyGoals || "Not provided. Skip Curricular Competency goals or state that none were selected."}

---
All other student information (use for baseline statements and to align objectives — e.g. reports, ISPP, designation, strengths/needs):
${allStudentData || "No additional data provided."}

---
Draft 3–5 measurable, strength-based goals: under each chosen competency above, generate 1–2 specific, measurable objectives. For every objective include: Methods of measurement, Current level of performance (baseline) from the student information above, and Proficiency scale (Emerging → Developing → Proficient → Extending).`;

  try {
    const response = await axios.post(
      `${API_URL_BASE}/api/chatgpt/openAI/completions`,
      {
        model: GENERATE_WITH_AI_MODEL,
        reasoning_effort: "low",
        messages: [
          { role: "system", content: MEASURABLE_GOALS_SYSTEM },
          { role: "user", content: userPrompt }
        ],
        max_completion_tokens: 2000,
      },
      await getLearningSupportsRequestConfig()
    );
    const content = getMessageContentFromResponse(response);
    return (content || "").trim();
  } catch (error) {
    console.error("Error generating measurable goals:", error);
    throw error;
  }
};

/**
 * Generate supports, accommodations, strategies, and services for Q11.
 */
export const generateSupportsServices = async ({ answers = {}, strengthsNeedsTable = {}, questionsList = [] }) => {
  ensureLearningSupportsApiConfigured();
  const getAnswer = (keyOrQuestion) => {
    const q = questionsList.find((x) => x.key === keyOrQuestion || x.question === keyOrQuestion);
    if (!q) return "";
    return (answers[q.question] || "").trim();
  };

  const designation = getAnswer("designation_category_funding") || getAnswer("student_profile_primary_designation") || "";
  const q7StrengthsNeeds = getAnswer("strengths_needs_designation_aligned") || "";
  const q9Goals = getAnswer("measurable_strength_based_goals") || "";
  const addOnPlans = getAnswer("add_on_plan_requirements") || "";

  const tableText = buildStrengthsNeedsTableText(strengthsNeedsTable);

  const systemPrompt = SUPPORTS_SERVICES_SYSTEM;

  const userPrompt = `Student's designation: ${designation || "Not specified"}

Strengths and needs (Q7): ${q7StrengthsNeeds || "Not provided"}

Measurable goals (Q9): ${q9Goals || "Not provided"}

Add-on plans required: ${addOnPlans || "Not specified"}

Strengths/needs by domain: ${tableText || "Not provided"}

Generate supports, accommodations, strategies, and services.`;

  try {
    const response = await axios.post(
      `${API_URL_BASE}/api/chatgpt/openAI/completions`,
      {
        model: GENERATE_WITH_AI_MODEL,
        reasoning_effort: "low",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt }
        ],
        max_completion_tokens: 1200,
      },
      await getLearningSupportsRequestConfig()
    );
    const content = getMessageContentFromResponse(response);
    return (content || "").trim();
  } catch (error) {
    console.error("Error generating supports/services:", error);
    throw error;
  }
};

